import { Component } from '@theme/component'
import { DiscountUpdateEvent } from '@theme/events'
import { cartPerformance } from '@theme/performance'
import { morphSection } from '@theme/section-renderer'
import { fetchConfig } from '@theme/utils'

class CartDiscount extends Component {
  requiredRefs = [
    'cartDiscountError',
    'cartDiscountErrorDiscountCode',
    'cartDiscountErrorShipping'
  ]

  #activeFetch = null

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort()
    }
    const abortController = new AbortController()
    this.#activeFetch = abortController
    return abortController
  }

  applyDiscount = async (event) => {
    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorShipping
    } = this.refs

    event.preventDefault()
    event.stopPropagation()

    const form = event.target
    if (!(form instanceof HTMLFormElement)) {
      return
    }

    const discountCode = form.querySelector('input[name="discount"]')
    if (
      !(discountCode instanceof HTMLInputElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return
    }

    const sectionId = this.dataset.sectionId
    const discountCodeValue = discountCode.value

    const abortController = this.#createAbortController()

    try {
      const existingDiscounts = this.#existingDiscounts()
      if (existingDiscounts.includes(discountCodeValue)) {
        return
      }

      cartDiscountError.classList.add('hidden')
      cartDiscountErrorDiscountCode.classList.add('hidden')
      cartDiscountErrorShipping.classList.add('hidden')

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [sectionId]
        })
      })

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal
      })

      const data = await response.json()

      if (data.status === 422 || data.errors) {
        throw new Error(data.message || data.description || 'Cart update error')
      }

      if (
        data.discount_codes?.find((discount) => {
          return (
            discount.code === discountCodeValue && discount.applicable === false
          )
        })
      ) {
        discountCode.value = ''
        this.#handleDiscountError('discount_code')
        return
      }

      const newHtml = data.sections[sectionId]
      const section = new DOMParser()
        .parseFromString(newHtml, 'text/html')
        .getElementById(`shopify-section-${sectionId}`)
      const discountCodes =
        section?.querySelectorAll('.cart-discount__pill') || []
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) =>
            element instanceof HTMLLIElement
              ? element.dataset.discountCode
              : null
          )
          .filter(Boolean)
        if (
          codes.length === existingDiscounts.length &&
          codes.every((code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((discount) => {
            return (
              discount.code === discountCodeValue &&
              discount.applicable === true
            )
          })
        ) {
          this.#handleDiscountError('shipping')
          discountCode.value = ''
          return
        }
      }

      document.dispatchEvent(
        new DiscountUpdateEvent(data, sectionId, {
          preventAutoOpen: true
        })
      )
      morphSection(sectionId, newHtml)
    } catch {
      this.#handleDiscountError('discount_code')
    } finally {
      this.#activeFetch = null
      cartPerformance.measureFromEvent('discount-update:user-action', event)
    }
  }

  removeDiscount = async (event) => {
    event.preventDefault()
    event.stopPropagation()

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return
    }

    const sectionId = this.dataset.sectionId

    const pill = event.target.closest('.cart-discount__pill')
    if (!(pill instanceof HTMLLIElement)) {
      return
    }

    const discountCode = pill.dataset.discountCode
    if (!discountCode) {
      return
    }

    const existingDiscounts = this.#existingDiscounts()
    const index = existingDiscounts.indexOf(discountCode)
    if (index === -1) {
      return
    }

    existingDiscounts.splice(index, 1)
    pill.classList.add('is-removing')

    const abortController = this.#createAbortController()

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: existingDiscounts.join(','),
          sections: [sectionId]
        })
      })

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal
      })

      const data = await response.json()

      if (data.status === 422 || data.errors) {
        throw new Error(data.message || data.description || 'Cart update error')
      }

      document.dispatchEvent(
        new DiscountUpdateEvent(data, sectionId, {
          morphed: true,
          preventAutoOpen: true
        })
      )
      morphSection(sectionId, data.sections[sectionId])
    } catch {
      this.#handleDiscountError('discount_code')
    } finally {
      this.#activeFetch = null
    }
  }

  #handleDiscountError(type) {
    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorShipping
    } = this.refs
    cartDiscountErrorDiscountCode.classList.add('hidden')
    cartDiscountErrorShipping.classList.add('hidden')
    const target =
      type === 'discount_code'
        ? cartDiscountErrorDiscountCode
        : cartDiscountErrorShipping
    cartDiscountError.classList.remove('hidden')
    target.classList.remove('hidden')
  }

  #existingDiscounts() {
    const discountCodes = []
    const discountPills = this.querySelectorAll('.cart-discount__pill')
    for (const pill of discountPills) {
      if (
        pill instanceof HTMLLIElement &&
        typeof pill.dataset.discountCode === 'string'
      ) {
        discountCodes.push(pill.dataset.discountCode)
      }
    }
    return discountCodes
  }
}

if (!customElements.get('ui-cart-discount')) {
  customElements.define('ui-cart-discount', CartDiscount)
}
